import {
    afterNextRender,
    Component,
    computed,
    Input,
    ChangeDetectionStrategy,
    HostListener,
    HostBinding,
    Output,
    EventEmitter,
    ElementRef,
    signal,
} from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { S25IconComponent } from "../s25-icon/s25.icon.component";
import { SidebarStore } from "./s25.sidebar.store";
import { S25IconButtonComponent } from "../../standalone/s25.icon.button.component";
import { S25Util } from "../../util/s25-util";
import { S25ButtonComponent } from "../../standalone/s25.button.component";

@TypeManagerDecorator("s25-ng-sidebar")
@Component({
    selector: "s25-ng-sidebar",
    template: `
        @if (mode === "overlay") {
            @if (buttonText) {
                <button class="aw-button aw-button--outline" (click)="sidebarStore.toggle()">{{ buttonText }}</button>
            }
            <div class="sidebar-overlay" [class.visible]="slidOpen()" (click)="onClose()"></div>
        }

        <div
            class="sidebar"
            [class.open]="slidOpen()"
            [class.push]="mode === 'push'"
            [style.--sidebar-width.px]="width"
            [attr.aria-hidden]="sidebarStore.isOpen() ? null : true"
            [inert]="!sidebarStore.isOpen()"
        >
            <div class="sidebar-header">
                <h2>{{ title }}</h2>
                <s25-ng-icon-button
                    class="s25-modal--close"
                    [type]="'close'"
                    (clicked)="onClose()"
                    [ariaLabel]="'Close sidebar'"
                >
                </s25-ng-icon-button>
            </div>

            <div [id]="sidebarStore.elementId" class="sidebar-content">
                <ng-content></ng-content>
            </div>
        </div>

        @if (mode === "push" && sidebarStore.tabVisible()) {
            <s25-ng-button
                class="sidebar-tab"
                [type]="'none'"
                (clicked)="sidebarStore.toggle()"
                [class.open]="slidOpen()"
                [ariaExpanded]="sidebarStore.isOpen()"
                [ariaControls]="sidebarStore.elementId"
            >
                <div class="sidebar-tab--buttons">
                    <s25-ng-icon
                        class="sidebar-tab-icon"
                        [class.open]="slidOpen()"
                        [type]="'doubleArrowLeft'"
                        [ariaLabel]="'Open Sidebar'"
                    />
                    <s25-ng-icon [type]="'aiSparkles'" [ariaLabel]="'AI assistance is available'" />
                </div>
            </s25-ng-button>
        }
    `,
    styles: [
        `
            :host {
                display: block;
            }

            :host.push {
                height: 100%;
                display: flex;
                flex-direction: row-reverse;
                align-items: flex-start;
                width: 0;
                transition: transform 0.5s ease;
            }

            :host.push.open {
                width: fit-content;
            }

            .sidebar-overlay {
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background-color: rgba(0, 0, 0, 0.1);
                z-index: 1000;
                opacity: 0;
                visibility: hidden;
                transition:
                    opacity 0.3s ease,
                    visibility 0.3s ease;
            }

            .sidebar-overlay.visible {
                opacity: 1;
                visibility: visible;
            }

            .sidebar {
                position: fixed;
                top: 0;
                background-color: var(--color--surface-bg);
                z-index: 1001;
                transform: translateX(100%);
                display: flex;
                flex-direction: column;
                transition: transform 0.5s ease;
            }

            .sidebar:not(.push) {
                right: 0;
                height: 100vh;
                width: min(var(--sidebar-width, 400px), 100vw);
            }

            .sidebar.open {
                transform: translateX(0);
                box-shadow: -2px 0 8px rgba(0, 0, 0, 0.15);
            }

            .sidebar.push {
                position: sticky;
                top: 10%;
                width: 0;
                overflow: hidden;
            }

            .sidebar.push.open {
                width: min(var(--sidebar-width, 400px), 100%);
            }

            .sidebar-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 1rem;
            }

            .sidebar-content {
                flex: 1;
                padding: 1rem;
                overflow-y: auto;
            }

            @media (max-width: 480px) {
                .sidebar:not(.push) {
                    height: 100%;
                    padding: env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom)
                        env(safe-area-inset-left);
                }

                .sidebar-content {
                    padding: 0.5rem;
                }
            }

            .sidebar-tab {
                display: grid;
                position: sticky;
                margin-right: -1.5rem;
                top: 10%;
                max-height: fit-content;
                background: var(--cn-blue);
                border: 1px solid rgba(0, 0, 0, 0.08);
                border-radius: 4px 0 0 4px;
                padding: 0.5rem;
                cursor: pointer;
                color: #fff;
                z-index: 1002;

                transition: margin-right 0.5s ease;
            }

            .sidebar-tab.open {
                margin-right: 0;
                transition: transform 0.5s ease;
            }

            .sidebar-tab--buttons {
                display: grid;
            }

            .sidebar-tab-icon.open {
                --icon--rotation-degrees: 180deg;
            }
        `,
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25IconComponent, S25IconButtonComponent, S25ButtonComponent],
})
export class S25SidebarComponent {
    @Input() title = "";
    @Input() buttonText = "";
    @Input() mode: "overlay" | "push" = "overlay";
    @Input() width = 400;

    @Output() closed = new EventEmitter<void>();

    /**
     * Whether the panel has been on screen closed, which is what the slide transitions away from.
     * A sidebar created already open - the organization's Invoices tab mounts its compose panel
     * and opens it in the same pass - has no closed frame to leave, and snaps into place instead.
     */
    private readonly painted = signal(false);

    /**
     * Drives only the classes that animate. Focus and screen readers follow the store directly, so
     * holding the slide back a frame never holds back the panel actually being open.
     */
    protected readonly slidOpen = computed(() => this.painted() && this.sidebarStore.isOpen());

    constructor(
        public sidebarStore: SidebarStore,
        private elRef: ElementRef,
    ) {
        afterNextRender(() => requestAnimationFrame(() => this.painted.set(true)));
    }

    onClose() {
        this.closed.emit();
        this.sidebarStore.close();
    }

    @HostListener("document:keydown.escape")
    @HostListener("window:popstate")
    handleEscape() {
        if (!this.sidebarStore.isOpen()) return;
        this.onClose();
    }

    @HostListener("focusout", ["$event"])
    onFocusOut(event: FocusEvent) {
        // keep keyboard focus in sidebar until closed
        const relatedTarget = event.relatedTarget;
        const popupOpen = S25Util.isDefined(document.querySelector(".s25-multiselect-popup-container"));
        if (
            !popupOpen &&
            relatedTarget &&
            this.sidebarStore.isOpen() &&
            !this.elRef.nativeElement.contains(relatedTarget)
        ) {
            const closeButton = this.elRef.nativeElement.querySelector(".s25-modal--close button");
            closeButton?.focus();
        }
    }

    @HostBinding("class.push")
    get pushMode() {
        return this.mode === "push";
    }

    @HostBinding("class.open")
    get isOpen() {
        return this.slidOpen();
    }
}
